import { JSX, type ReactNode, useEffect, useState } from "react";

import { Box, Divider, Theme, useTheme } from "@mui/material";
import { isMobile } from "react-device-detect";

import TopNavRightBox from "./components/TopNavRightBox";

function getStyles(theme: Theme) {
  const common = {
    display: "flex",
    alignItems: "center",
    width: "100%",
  };
  return {
    locationBox: {
      ...common,
      justifyContent: "space-between",
    },
    container: {
      ...common,
      px: { xs: 1, md: 0 },
      justifyContent: "space-between",
      flexDirection: { xs: "column-reverse", md: "row" },
      gap: { xs: 1, md: 0 },
    },
    leftBox: {
      ...common,
      gap: 2,
      justifyContent: { xs: "flex-start", md: "flex-start" },
    },
    leftBoxDefault: {
      ...common,
      gap: 0,
      justifyContent: { xs: "flex-start", md: "flex-start" },
    },
    rightBox: {
      ...common,
      justifyContent: { xs: "flex-end", md: "flex-end" },
      gap: 1,
      mt: { xs: 0, md: 0 },
    },
    childrenBox: {
      width: "100%",
    },
    dividerStyles: {
      display: { xs: "none", md: "block" },
      mt: 2,
      borderBottomWidth: 2,
      mb: 4,
      borderColor: theme.palette.border.default,
    },
  };
}

interface TopNavProps {
  children: ReactNode;
  selectedLocation?: JSX.Element | null;
  togglePinned?: JSX.Element | null;
  refreshButton?: JSX.Element | null;
}

function TopNav({ children, selectedLocation = null, togglePinned = null, refreshButton = null }: TopNavProps): JSX.Element {
  const theme = useTheme();
  const styles = getStyles(theme);
  const hasToggle = !!togglePinned;

  const [isMobileAdjusted, setIsMobileAdjusted] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsMobileAdjusted(isMobile && window.innerWidth < 1024);
    }
  }, []);

  const renderMobileLayout = () => (
    <>
      <Box sx={styles.locationBox}>
        <Box>{selectedLocation}</Box>
        <Box>{togglePinned}</Box>
      </Box>
      <Box sx={styles.childrenBox}>{children}</Box>
      <Box sx={styles.rightBox}>
        <TopNavRightBox />
      </Box>
    </>
  );

  const renderDesktopLayout = () => (
    <>
      <Box sx={styles.leftBox}>
        {selectedLocation}
        {togglePinned}
      </Box>
      <Box sx={styles.rightBox}>
        {children}
        <TopNavRightBox />
      </Box>
    </>
  );

  const renderDefaultLayout = () => (
    <>
      <Box sx={styles.leftBoxDefault}>
        {children}
        {selectedLocation === null && togglePinned === null && refreshButton}
      </Box>
      <Box sx={styles.rightBox}>
        <TopNavRightBox />
      </Box>
    </>
  );
  return (
    <>
      <Box sx={styles.container}>
        {hasToggle ? (isMobileAdjusted ? renderMobileLayout() : renderDesktopLayout()) : renderDefaultLayout()}
      </Box>
      <Divider sx={styles.dividerStyles} />
    </>
  );
}

export default TopNav;
